import {
  ApacCandidateHeader,
  ApacCandidateFooter,
} from '@seek/apac-candidate-header-footer';
import type { SiteName } from '@seek/melways-sites';
import {
  id_jobstreet_com,
  th_jobsdb_com,
  hk_jobsdb_com,
} from '@seek/online-identity';
import { Box, PageBlock, ToastProvider } from 'braid-design-system';
import type { ReactNode } from 'react';

import { Banner } from 'src/components/atoms/Banner/Banner';
import GroupFooter from 'src/components/atoms/GroupFooter/GroupFooter';
import LoginModal from 'src/components/atoms/LoginModal/LoginModal';
import { NAV_PATHS } from 'src/paths';
import { useAuthStatus } from 'src/provider/auth';
import { useConfig } from 'src/provider/config';
import { LoginModalProvider } from 'src/provider/loginModal';

interface Props {
  children: ReactNode;
}

const getLanguageSwitcher = (isSiteName: SiteName, urlWithoutLang: string) => {
  const siteLanguageMap: Record<string, string[]> = {
    [id_jobstreet_com.siteName as string]: ['en', 'id'],
    [th_jobsdb_com.siteName as string]: ['en', 'th'],
    [hk_jobsdb_com.siteName as string]: ['en', 'zh'],
  };

  const languages = siteLanguageMap[isSiteName];
  if (!languages) return {};

  return Object.fromEntries(
    languages.map((lang) => [
      lang,
      {
        url: lang === 'en' ? urlWithoutLang : `/${lang}${urlWithoutLang}`,
      },
    ]),
  );
};

export function Layout({ children }: Props) {
  const { site, absoluteUrl } = useConfig();
  const { authStatus, userProfile } = useAuthStatus();
  const { pathname: currentUrl } = new URL(absoluteUrl);

  const urlWithoutLang = currentUrl.replace(/^\/(en|id|th|zh)/, '');
  const languageSwitcher: Partial<Record<string, { url: string }>> =
    getLanguageSwitcher(site, urlWithoutLang);
  const handleLanguageSwitch = (lang: string) => {
    if (languageSwitcher[lang]) {
      window.location.href = languageSwitcher[lang].url;
    }
  };

  return (
    <>
      <ToastProvider>
        <LoginModalProvider>
          <ApacCandidateHeader
            authenticationStatus={authStatus}
            userProfile={userProfile}
            languageSwitcher={languageSwitcher}
            onLanguageSwitch={handleLanguageSwitch}
            returnUrlForPostAuth={NAV_PATHS.communityHome}
            leanHeader={false}
            widthMode="large"
            activePrimaryTab="community"
          />
          <Banner />
          <PageBlock width="large">
            <Box>{children}</Box>
            <LoginModal />
          </PageBlock>
          <GroupFooter />
          <ApacCandidateFooter
            authenticationStatus={authStatus}
            isExpandedVersion={true}
          />
        </LoginModalProvider>
      </ToastProvider>
    </>
  );
}
